import type {
  FenixBackupV1,
} from '../domain/backup.ts';

import {
  validateRestoreBackup,
} from '../domain/restore-backup.ts';

import {
  validRestorePreviewToken,
} from '../domain/restore.ts';

import type {
  RestorePreviewToken,
  RestoreRecoveryOutcome,
  RestoreResult,
} from '../domain/restore.ts';

import type {
  RestoreRepository,
} from '../ports/restore-repository.ts';

export interface RestoreEnvironment {
  operationId(): string;
  datasetId(): string;
  now(): string;
}

export interface RestoreSummary {
  backupId: string;
  producerAppVersion: string;
  exportedAt: string;

  counts: {
    training: number;
    nutrition: number;
    progress: number;
    dailyRoutine: number;
    history: number;
  };
}

export interface PreparedRestore {
  preview: RestorePreviewToken;
  summary: RestoreSummary;
}

function failure<T = never>(
  code:
    | 'RESTORE_INVALID_BACKUP'
    | 'RESTORE_SCHEMA_FUTURE'
    | 'RESTORE_PREVIEW_STALE'
    | 'RESTORE_IN_PROGRESS'
    | 'RESTORE_OPERATION_CONFLICT'
    | 'RESTORE_CORRUPTION'
    | 'STORAGE_QUOTA'
    | 'TRANSACTION_ABORTED'
    | 'STORAGE_UNAVAILABLE',
): RestoreResult<T> {
  return {
    ok: false,
    code,
  };
}

function summary(
  backup: FenixBackupV1,
): RestoreSummary {
  return {
    backupId:
      backup.backupId,

    producerAppVersion:
      backup.producerAppVersion,

    exportedAt:
      backup.exportedAt,

    counts: {
      training:
        backup.dataset.training.exercises.length
        + backup.dataset.training.routines.length
        + backup.dataset.training.plannedSessions.length
        + backup.dataset.training.executions.length
        + backup.dataset.training.workingSets.length,

      nutrition:
        backup.dataset.nutrition.recipes.length
        + backup.dataset.nutrition.plannedMeals.length
        + backup.dataset.nutrition.consumptions.length
        + backup.dataset.nutrition.shoppingItems.length,

      progress:
        backup.dataset.progress.weights.length,

      dailyRoutine:
        backup.dataset.dailyRoutine.checks.length,

      history:
        backup.revisionHistory.dailyRoutine.checks.length
        + backup.revisionHistory.training.exercises.length
        + backup.revisionHistory.training.routines.length
        + backup.revisionHistory.training.plannedSessions.length
        + backup.revisionHistory.nutrition.recipes.length
        + backup.revisionHistory.nutrition.plannedMeals.length
        + backup.revisionHistory.nutrition.consumptions.length
        + backup.revisionHistory.nutrition.shoppingItems.length
        + backup.revisionHistory.progress.weights.length,
    },
  };
}

export class RestoreManager {
  private readonly repository:
    RestoreRepository;

  private readonly environment:
    RestoreEnvironment;

  constructor(
    repository: RestoreRepository,
    environment: RestoreEnvironment,
  ) {
    this.repository =
      repository;

    this.environment =
      environment;
  }

  async prepareJson(
    json: string,
  ): Promise<
    RestoreResult<PreparedRestore>
  > {
    if (
      typeof json !== 'string'
      || json.trim().length === 0
    ) {
      return failure(
        'RESTORE_INVALID_BACKUP',
      );
    }

    let parsed: unknown;

    try {
      parsed =
        JSON.parse(json);
    }
    catch {
      return failure(
        'RESTORE_INVALID_BACKUP',
      );
    }

    const validated =
      validateRestoreBackup(
        parsed,
      );

    if (!validated.ok) {
      return validated;
    }

    const restoreOperationId =
      this.environment
        .operationId();

    const stagingDatasetId =
      this.environment
        .datasetId();

    const now =
      this.environment.now();

    const staged =
      await this.repository.stage({
        backup:
          validated.value,

        restoreOperationId,
        stagingDatasetId,
        now,
      });

    if (!staged.ok) {
      return staged;
    }

    return {
      ok: true,
      value: {
        preview:
          staged.value,

        summary:
          summary(
            validated.value,
          ),
      },
    };
  }

  async confirm(
    prepared: PreparedRestore,
  ): Promise<
    RestoreResult<{
      status: 'COMPLETED';
      activeDatasetId: string;
    }>
  > {
    if (
      !prepared
      || !validRestorePreviewToken(
        prepared.preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    const applied =
      await this.repository.apply(
        prepared.preview,
        this.environment.now(),
      );

    if (!applied.ok) {
      if (
        applied.code
          === 'RESTORE_PREVIEW_STALE'
      ) {
        await this.repository
          .discard(
            prepared.preview,
            this.environment.now(),
          );
      }

      return applied;
    }

    if (
      applied.value.status
        === 'ROLLED_BACK'
    ) {
      return failure(
        'RESTORE_CORRUPTION',
      );
    }

    return {
      ok: true,
      value: {
        status: 'COMPLETED',
        activeDatasetId:
          applied.value.activeDatasetId,
      },
    };
  }

  async cancel(
    prepared: PreparedRestore,
  ): Promise<RestoreResult<void>> {
    if (
      !prepared
      || !validRestorePreviewToken(
        prepared.preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    return this.repository
      .discard(
        prepared.preview,
        this.environment.now(),
      );
  }

  pendingPreview():
    Promise<
      RestoreResult<
        RestorePreviewToken | null
      >
    > {
    return this.repository
      .pendingPreview();
  }

  async discardPending(
    preview: RestorePreviewToken,
  ): Promise<RestoreResult<void>> {
    if (
      !validRestorePreviewToken(
        preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    return this.repository
      .discard(
        preview,
        this.environment.now(),
      );
  }

  recover():
    Promise<
      RestoreResult<RestoreRecoveryOutcome>
    > {
    return this.repository
      .recover(
        this.environment.now(),
      );
  }
}